import React from "react"
import * as queryString from "query-string"
import { buildDefaultSearchParams } from "../../features/search/services/searchParams"
import SerpContainer from "./SerpContainer"
import { BreadcrumbSegment } from "../../ui/organisms/HeroBreadcrumb"
import { Tag } from "../../types/tag"
import { SearchControllerParams } from "../../features/search/types"
import { SearchController } from "../../features/search/components/SearchController"
import { groupTags } from "../../utils/tags"
import { useRootContents } from "../../aggregators/rootData"
import { AvailabilitySearchFilters, AvailabilitySearchSorting } from "./types"

const createDefaultSearchParams = (
  categoryCodes: string[] | undefined,
  brandCodes: string[] | undefined,
  excludeBrandCodes: string[] | undefined,
  tags: Tag[] | undefined,
  languageId: string,
  // todo: remove
  layoutData: any
): SearchControllerParams =>
  buildDefaultSearchParams(
    {
      languageId,
      categoryCodes,
      brandCodes,
      excludeBrandCodes,
      tags,
      queryParams: queryString.parse(location.search),
    },
    layoutData
  )

interface Props {
  title: string
  subtitle?: React.ReactNode
  description?: React.ReactNode
  descriptionExpandable?: boolean
  intro?: React.ReactNode
  breadcrumb: BreadcrumbSegment[]
  heroLogo?: React.ReactNode
  heroBackgroundImage?: string
  heroBackgroundImageMobile?: string
  categoryCodes?: string[]
  brandCodes?: string[]
  excludeBrandCodes?: string[]
  minDiscountPercent?: number
  maxDiscountPercent?: number
  tags?: Tag[]
  languageId: string
  contents: any
  hideHero?: boolean
  heroClassName?: string
}

const SerpContainerWrapper = ({
  categoryCodes,
  brandCodes,
  excludeBrandCodes,
  tags,
  intro,
  languageId,
  contents,
  heroBackgroundImage,
  heroBackgroundImageMobile,
  breadcrumb,
  minDiscountPercent,
  maxDiscountPercent,
  title,
  subtitle,
  description,
  descriptionExpandable,
  heroLogo,
  hideHero,
  heroClassName,
}: Props) => {
  const [searchParams, setSearchParams] = React.useState<
    SearchControllerParams | undefined
  >()
  const [nextCursor, setNextCursor] = React.useState<string | undefined>()

  const { layout } = useRootContents()

  React.useEffect(() => {
    setSearchParams(
      createDefaultSearchParams(
        categoryCodes,
        brandCodes,
        excludeBrandCodes,
        tags,
        languageId,
        layout
      )
    )
  }, [categoryCodes, brandCodes])

  const updateFilters = (filters: AvailabilitySearchFilters) => {
    setSearchParams({
      ...(searchParams as SearchControllerParams),
      filters,
      paging: {
        newSearch: false,
        firstPage: true,
      },
    })
  }

  const updateSorting = (sorting: AvailabilitySearchSorting) =>
    setSearchParams({
      ...(searchParams as SearchControllerParams),
      sorting,
      paging: {
        newSearch: false,
        firstPage: true,
      },
    })

  const loadMoreResults = () => {
    setSearchParams({
      ...(searchParams as SearchControllerParams),
      paging: {
        newSearch: false,
        firstPage: false,
        cursor: nextCursor,
      },
    })
  }

  const initialFilters = searchParams
    ? {
        languageId: searchParams.filters.languageId,
        brandCodes,
        excludeBrandCodes,
        categories: categoryCodes,
        tags: groupTags(tags ?? []),
        minDiscountPercent,
        maxDiscountPercent,
      }
    : undefined

  return (
    <SearchController
      searchParams={searchParams}
      initialFilters={initialFilters}
      onNextCursorChange={setNextCursor}
    >
      {/* {initialized ? ( */}
      <SerpContainer
        searchParams={searchParams}
        onFiltersChange={updateFilters}
        onSortingChange={updateSorting}
        onLoadNextResultsPage={loadMoreResults}
        breadcrumb={breadcrumb}
        heroBackgroundImage={heroBackgroundImage}
        heroBackgroundImageMobile={heroBackgroundImageMobile}
        contents={contents}
        title={title}
        subtitle={subtitle}
        description={description}
        descriptionExpandable={descriptionExpandable}
        intro={intro}
        hideHero={hideHero}
        heroLogo={heroLogo}
        heroClassName={heroClassName}
      />
      {/* ) : undefined} */}
    </SearchController>
  )
}

export default SerpContainerWrapper
